import { Inject, Injectable, NgZone } from '@angular/core';
import { Firestore, doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { from, firstValueFrom, map, Observable } from 'rxjs';
import { observeInZone } from '../utils/ZoneUtils';
import { FIRESTORE } from '../firebase.providers';

@Injectable({
  providedIn: 'root',
})
export class ChipService {
  // SEC-01: the signup code exists as a chip-ids doc ID (validated with a point-get,
  // exactly like a chip number, never readable as a field). This admin-only pointer
  // doc records the current code so the admin screen can show and rotate it.
  private readonly signupCodePointerPath = 'admin-config/signup-code';

  constructor(
    @Inject(FIRESTORE) private db: Firestore,
    private zone: NgZone,
  ) {}

  /** True when the value is a valid chip number OR the current signup code. Both
   *  live as chip-ids doc ids, so a single anonymous point-get covers both. */
  chipIdExists(chipId: string): Observable<boolean> {
    return from(getDoc(doc(this.db, 'chip-ids', chipId))).pipe(
      observeInZone(this.zone),
      map((snapshot) => snapshot.exists()),
    );
  }

  /** Rotates the signup code: removes the old code's chip-ids doc, creates the new
   *  one, and updates the admin-only pointer, atomically. Admin-only (rules). */
  async saveSignupCode(signupCode: string): Promise<void> {
    const currentCode = await firstValueFrom(this.getCurrentSignupCode());
    if (currentCode === signupCode) {
      return;
    }
    const batch = writeBatch(this.db);
    if (currentCode) {
      batch.delete(doc(this.db, 'chip-ids', currentCode));
    }
    batch.set(doc(this.db, 'chip-ids', signupCode), {
      id: signupCode,
      signupCode: true,
    });
    batch.set(doc(this.db, this.signupCodePointerPath), { id: signupCode });
    await batch.commit();
  }

  addNewChipId(chipId: string): Promise<void> {
    return setDoc(doc(this.db, 'chip-ids', chipId), { id: chipId });
  }

  getCurrentSignupCode(): Observable<string> {
    return from(getDoc(doc(this.db, this.signupCodePointerPath))).pipe(
      observeInZone(this.zone),
      map(
        (snapshot) =>
          (snapshot.data() as { id?: string } | undefined)?.id ?? '',
      ),
    );
  }
}
